import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AfterLessonReminderCard } from '../board/panels/AfterLessonReminderCard';
import { AttendanceModal } from '../board/panels/AttendanceModal';
import { ClassLogbookCard } from '../board/panels/ClassLogbookCard';
import { ConductCard } from '../board/panels/ConductCard';
import type { SlotAttendanceParams } from '../api/repository/slot-attendance';
import { subjectFallback } from '../board/periodLabels';
import type { BoardContextValue } from '../board/useBoardContext';
import type { AttendanceCounts } from '../stores/qlt/slot-attendance';
import { useQltClassNoteStore, useQltSlotAttendanceStore } from '../stores/qlt';
import { PaneScrim } from './PaneScrim';

const DONUT_RADIUS = 26;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;
// viewBox units; the hole is 45 across (r 26 minus half the 7 stroke), the label keeps 4 clear each side
const DONUT_LABEL_SIZE = 12;
const DONUT_LABEL_MAX = 37;

function isoWeekday(date: string): number {
  const day = new Date(`${date}T00:00:00Z`).getUTCDay();
  return day === 0 ? 7 : day;
}

/** The roster identity tuple. BoardShell loads with it too, so the collapsed Sĩ số widget is never blank. */
export function attendanceContextOf(context: Pick<BoardContextValue, 'classId' | 'index' | 'date'>): SlotAttendanceParams {
  return { class: context.classId, indexDay: `${context.index}-${isoWeekday(context.date)}`, date: context.date };
}

function AttendanceDonut({ percent }: { percent: number }) {
  const filled = (DONUT_CIRCUMFERENCE * percent) / 100;
  const label = useRef<SVGTextElement>(null);
  // the board renders text larger than its CSS size, so shrink the label until it clears the ring
  useLayoutEffect(() => {
    const text = label.current;
    if (!text) return;
    text.setAttribute('font-size', String(DONUT_LABEL_SIZE));
    const width = text.getBBox().width;
    if (width > DONUT_LABEL_MAX) text.setAttribute('font-size', String((DONUT_LABEL_SIZE * DONUT_LABEL_MAX) / width));
  }, [percent]);
  return (
    <div className="attendance__chart">
      <svg width="64" height="64" viewBox="0 0 62 62" aria-hidden={true}>
        <circle cx="31" cy="31" r={DONUT_RADIUS} fill="none" stroke="var(--color-donut-track)" strokeWidth="7" />
        <circle cx="31" cy="31" r={DONUT_RADIUS} fill="none" stroke="var(--color-present)" strokeWidth="7" strokeLinecap="round" strokeDasharray={`${filled} ${DONUT_CIRCUMFERENCE}`} transform="rotate(-90 31 31)" />
        <text ref={label} className="attendance__value" x="31" y="31" textAnchor="middle" dominantBaseline="central" fontSize={DONUT_LABEL_SIZE}>{percent}%</text>
      </svg>
    </div>
  );
}

/** Sĩ số card - owned by the shell; every other panel card is a mount point below. */
function AttendanceCard({ counts, loading, onOpen }: { counts: AttendanceCounts; loading: boolean; onOpen: () => void }) {
  const { t } = useTranslation('layout');
  // Floored, not rounded: Figma draws 35/40 as 87%, and a rounded 87.5 reads 88.
  const percent = counts.total ? Math.floor((counts.present / counts.total) * 100) : 0;
  return (
    <section className="card card--attendance">
      <div className="card-title-row">
        <div>
          <div className="card__title">{t('attendance.classSize')}</div>
          {/* a refresh behind the modal must not blank a roster that is already on screen */}
          <div className="card__subtitle">{loading && counts.total === 0 ? t('attendance.loadingClassSize') : t('attendance.studentsValue', { present: counts.present, total: counts.total })}</div>
        </div>
        <button type="button" className="card-link" onClick={onOpen}>{t('attendance.open')}</button>
      </div>
      <div className="attendance">
        <AttendanceDonut percent={percent} />
        <ul className="attendance__legend">
          <li><span className="attendance__dot" style={{ background: 'var(--color-present)' }} />{t('attendance.present', { value: counts.present })}</li>
          <li><span className="attendance__dot" style={{ background: 'var(--color-primary-deep)' }} />{t('attendance.absent', { value: counts.absent })}</li>
          {counts.late > 0 ? <li><span className="attendance__dot" style={{ background: 'var(--tag-test-fg)' }} />{t('attendance.late', { value: counts.late })}</li> : null}
        </ul>
      </div>
    </section>
  );
}

// The back arrow is the only way to end a lesson, so the footer link collapses the panel.
export function RightPanel({ context, onCollapse }: { context: BoardContextValue; onCollapse: () => void }) {
  const { t } = useTranslation('layout');
  const attendance = useQltSlotAttendanceStore();
  const loadNote = useQltClassNoteStore((state) => state.loadNote);
  const loadLesson = useQltClassNoteStore((state) => state.loadLesson);
  const clearClassNote = useQltClassNoteStore((state) => state.clear);
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const attendanceContext = useMemo(
    () => attendanceContextOf(context),
    [context.classId, context.date, context.index],
  );

  useEffect(() => {
    if (!context.classNoteId) {
      clearClassNote();
      return;
    }
    void loadNote(context.classNoteId);
    void loadLesson(context.classNoteId);
  }, [clearClassNote, context.classNoteId, loadLesson, loadNote]);

  return (
    <>
      {/* same dismissal as panel__close and panel__end, with the affordance made visible */}
      <PaneScrim onDismiss={onCollapse} />
      <aside className="panel" aria-label={t('panel.title')}>
        <header className="panel__header">
          <h2 className="panel__title">{t('panel.title')}</h2>
          <p className="panel__subtitle">{context.periodLabel} • {context.subjectTitle || subjectFallback()}</p>
          {/* the footer Đóng lại is a whole panel scroll away - this is the same action at the top */}
          <button type="button" className="panel__close" aria-label={t('panel.closeAria')} onClick={onCollapse}>×</button>
        </header>

        <AttendanceCard
          counts={attendance.counts}
          loading={attendance.loading}
          onOpen={() => setAttendanceOpen(true)}
        />

        {/* MOUNT POINT - class-management agent owns the three cards below */}
        <ClassLogbookCard periodLabel={context.periodLabel} classTitle={context.classTitle} subjectTitle={context.subjectTitle} lessonTitle={context.lessonTitle} classNoteId={context.classNoteId} />
        <ConductCard classNoteId={context.classNoteId} classId={context.classId} classTitle={context.classTitle} subjectId={context.subjectId} date={context.date} periodLabel={context.periodLabel} subjectTitle={context.subjectTitle} />
        <AfterLessonReminderCard periodLabel={context.periodLabel} date={context.date} subjectTitle={context.subjectTitle} lessonTitle={context.lessonTitle} classNoteId={context.classNoteId} classId={context.classId} gradeId={context.gradeId} subjectId={context.subjectId} />
        {/* END MOUNT POINT */}

        <button type="button" className="panel__end" onClick={onCollapse}>{t('panel.close')}</button>
      </aside>
      <AttendanceModal
        open={attendanceOpen}
        classTitle={context.classTitle}
        context={attendanceContext}
        onClose={() => setAttendanceOpen(false)}
      />
    </>
  );
}
